<template>
    <div
        :class="componentClasses"
    >
        <div class="notification-block__prefix">
            <svg-icon
                :icon="WifiExclamation"
                is-custom-icon
                :color="opened ? 'green-500' : 'orange-500'"
            />
        </div>
        <div class="notification-block__content">
            <template v-if="opened">
                <text-decorator
                    class="notification-block__title"
                    bold
                >
                    We hebben de realtime verbinding hersteld!
                </text-decorator>
                Deze melding verdwijnt automatisch.
            </template>
            <template v-else>
                <text-decorator
                    v-if="props.title"
                    class="notification-block__title"
                    bold
                >
                    {{ props.title }}
                </text-decorator>
                {{ props.text ? props.text : '' }}
            </template>
        </div>
        <div class="notification-block__button">
            <button type="button" @click="handleClick">
                Opnieuw verbinden
            </button>
        </div>
    </div>
</template>

<script lang="ts" setup>
    import { computed, onMounted, ref } from 'vue';
    import { useRender } from '@keyvue/one-ui';
    import MercureConnection from '@/services/keyvue-api/mercure/MercureConnection';
    import WifiExclamation from '@keyvue/one-awesome-icons/icons/solid/wifi-exclamation.svg?component';
    import useEventBus from '@/services/eventbus/composables/useEventBus';
    import MercureConnectionOpenedEvent from '@/services/keyvue-api/mercure/MercureConnectionOpenedEvent';

    export interface Props {
        title: string
        connectionName: string
        text?: string
    }

    const props = withDefaults(defineProps<Props>(), {
        text: '',
    });

    const eventBus = useEventBus();

    const opened = ref(false);

    onMounted(() => {
        eventBus.subscribe(MercureConnectionOpenedEvent(props.connectionName), onConnectionOpened);
    });

    function onConnectionOpened() {
        opened.value = true;

        setTimeout(() => {
            unRender.value();
        }, 5000);
    }

    async function handleClick() {
        MercureConnection.getInstance(props.connectionName).ensureConnection();
    }

    const {
        rendered,
        setUnRender,
        unRender,
        unRendered,
        unRenderDelay,
        onRender,
        onUnRender,
    } = useRender(200);

    const componentClasses = computed(() => ({
        'notification-block': true,
        'notification-block--opened': opened.value,
        'notification-block--rendered': rendered.value,
        'notification-block--un-rendered': unRendered.value,
    }));

    defineExpose({
        setUnRender,
        onRender,
        onUnRender,
        unRenderDelay: unRenderDelay.value,
    });
</script>

<style lang="scss" scoped>
.notification-block {
    @include box-shadow-360();

    pointer-events: auto;
    cursor: pointer;

    transform: translateX(50px);

    display: flex;
    flex-wrap: wrap;
    gap: size(sm);

    box-sizing: border-box;
    width: 100%;
    padding: size(md);

    font-size: font-size(xs);
    color: var(--theme-colors-default--gray-800);

    opacity: 0;
    background-color: var(--theme-colors-default--white);
    border-bottom: 2px solid var(--theme-colors-default--orange-500);
    border-radius: size(xs);

    transition: transform .2s, opacity .2s;

    &__content {
        display:flex;
        flex-direction: column;
        gap: size(xs);
        justify-content: center;
        width: calc(100% - 24px - size(sm));
    }

    &__title {
        color: var(--theme-colors-default--orange-500);
    }

    &__prefix {
        display: flex;
        align-items: flex-start;
        height: 100%;
        width: 24px;
    }

    &__button {
        display: flex;
        justify-content: flex-end;
        padding-top: size(sm);
        width: 100%;

        & > button {
            background-color: var(--theme-colors-default--orange-500);
            border: 0;
            padding: 5px 10px;
            color: var(--theme-colors-default--white);
            font-weight: bold;
            border-radius: size(xs);
            border: 1px solid var(--theme-colors-default--orange-500);

            &:hover {
                background-color: var(--theme-colors-default--white);
                color: var(--theme-colors-default--orange-500);
            }
        }
    }

    &--space-between {
        justify-content: space-between;
    }

    &--rendered {
        transform: translateX(0);
        opacity: 1;
    }

    &--un-rendered {
        transform: translateX(50px);
        opacity: 0;
    }

    &--opened {
        border-color: var(--theme-colors-default--green-500);

        .notification-block{
            &__button {
                display: none;
                pointer-events: none;
            }
            &__title {
                color: var(--theme-colors-default--green-500);
            }
        }
    }
}
</style>
